/**
 * The page's side of the thumbnail worker (workers/thumbnailWorker.ts): spawned on first use, one per page, jobs
 * matched to answers by id. Where there is no worker (no Worker or OffscreenCanvas, or it failed to start) every
 * call answers undefined and the caller does the work its own way.
 */

import type { ThumbJob, ThumbResult } from './thumbnailWorkerTypes';

/** How long a worker thumbnail may take before it counts as failed (ms). */
const THUMB_WORKER_TIMEOUT_MS = 20000;
export type ThumbJobInput = DistributiveOmit<ThumbJob, 'id'>;
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

let thumbWorker: Worker | null = null;
let thumbWorkerBroken = false;
let thumbJobs = 0;
const thumbWaiting = new Map<number, (result: ThumbResult | undefined) => void>();

/** The thumbnail worker, spawned on first use; null where workers or OffscreenCanvas are missing (or it broke). */
function spawnThumbWorker(): Worker | null {
  if (thumbWorker || thumbWorkerBroken) return thumbWorker;
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') {
    thumbWorkerBroken = true;
    return null;
  }
  try {
    // Must stay exactly `new Worker(new URL(literal, import.meta.url), ...)`: Vite bundles only that form.
    thumbWorker = new Worker(new URL('../workers/thumbnailWorker.ts', import.meta.url), { type: 'module' });
  } catch {
    thumbWorkerBroken = true;
    return null;
  }
  thumbWorker.onmessage = (event: MessageEvent<ThumbResult>) => {
    const done = thumbWaiting.get(event.data.id);
    thumbWaiting.delete(event.data.id);
    done?.(event.data);
  };
  thumbWorker.onerror = () => {
    // A worker that cannot run (a bundling or browser gap): every waiting job fails over to the page's canvas.
    thumbWorkerBroken = true;
    thumbWorker?.terminate();
    thumbWorker = null;
    for (const done of thumbWaiting.values()) done(undefined);
    thumbWaiting.clear();
  };
  return thumbWorker;
}

/** `input` run in the worker (`transfer` moved to it): its result, or undefined when there is no worker. */
export function postThumbJob(input: ThumbJobInput, transfer: Transferable[] = []): Promise<ThumbResult | undefined> {
  const worker = spawnThumbWorker();
  if (!worker) return Promise.resolve(undefined);
  const id = ++thumbJobs;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      thumbWaiting.delete(id);
      resolve({ id, blob: null, error: 'timed out' });
    }, THUMB_WORKER_TIMEOUT_MS);
    thumbWaiting.set(id, (result: ThumbResult | undefined) => {
      clearTimeout(timer);
      resolve(result);
    });
    try {
      worker.postMessage({ ...input, id } as ThumbJob, transfer);
    } catch {
      clearTimeout(timer);
      thumbWaiting.delete(id);
      resolve(undefined);
    }
  });
}

/**
 * A thumbnail made in the worker, off the page's thread: its Blob (null when the picture could not be made), or
 * undefined when there is no worker or it cannot make this one (the caller makes it here). A frame given is the
 * worker's to close.
 */
export async function thumbInWorker(input: ThumbJobInput): Promise<Blob | null | undefined> {
  const result = await postThumbJob(input, input.kind === 'frame' ? [input.frame] : []);
  if (!result && input.kind === 'frame') input.frame.close();
  return !result || result.unsupported ? undefined : result.blob;
}

/** A decoded image's pixels (RGBA, top row first, not premultiplied) and size. */
export interface ImagePixels {
  pixels: Uint8Array;
  width: number;
  height: number;
}

/**
 * The image at `url` decoded in the worker to RGBA pixels no larger than `maxSide`: null when it can't be decoded,
 * undefined when the worker can't take it (no worker, an SVG): the caller decodes it its own way. Handed to the
 * sleepy-ui module as Module.suiImagePixels, whose upload of plain pixels needs no GPU-side image sharing.
 */
export async function imagePixels(url: string, maxSide: number): Promise<ImagePixels | null | undefined> {
  const absolute = new URL(url, window.location.href).href;
  const result = await postThumbJob({ kind: 'pixels', url: absolute, maxEdge: maxSide, type: '' });
  if (!result || result.unsupported) return undefined;
  if (!result.pixels || !result.width || !result.height) return null;
  return { pixels: new Uint8Array(result.pixels), width: result.width, height: result.height };
}
